/**
 * Gift Packaging Widget — Web Component
 * Custom isolated file — safe to copy to any theme version
 *
 * Native replacement for the "Wrap In" app embed.
 *
 * Behaviour:
 * - "Is this a gift?" switch reveals the widget body.
 * - Tapping the summary row opens the theme's native <x-modal> ("Your
 *   gifting options") listing the Premium Gift Kit (gift bag + card, printed
 *   message) and any card-only design options.
 * - If the Premium Gift Kit is chosen, a "Choose your card" grid + optional
 *   gift message field appear. Card-only options skip both (blank inside).
 * - On submit, this component does NOT touch the main product's add-to-cart
 *   request at all — it listens for the theme's own `cart:change` event
 *   (fired after the main product is successfully added) and then makes a
 *   second, separate /cart/add.js call to add the gift line item. This
 *   keeps the existing add-to-cart flow for every other product on the
 *   site completely untouched.
 */

class GiftPackaging extends HTMLElement {
  connectedCallback() {
    this.toggle = this.querySelector('.switch');
    this.body = this.querySelector('.gift-packaging__body');
    this.summaryText = this.querySelector('.gift-packaging__summary-text');
    this.summaryIcon = this.querySelector('.gift-packaging__summary-icon');
    this.modal = this.querySelector('x-modal');
    this.cardGrid = this.querySelector('[data-gift-card-grid]');
    this.messageField = this.querySelector('[data-gift-message-field]');
    this.errorEl = this.querySelector('[data-gift-error]');
    this.form = this.closest('form[action*="/cart/add"]');
    this.atcButton = this.form?.querySelector('button[name="add"]');
    this.basePrice = parseInt(this.dataset.basePrice, 10) || 0;
    this.selectedGiftPrice = 0;

    if (this.atcButton && !this.atcButton.disabled) {
      // Parse the original server-rendered label once, e.g. "Add to cart - $119.00",
      // so we can rebuild it with an updated total while keeping the same
      // wording/currency symbol the theme already rendered. The prefix
      // capture group already includes the currency symbol itself.
      const match = this.atcButton.textContent.match(/^(.*[^\d.,\s])\s*[\d.,]+\s*$/);
      if (match) {
        this.atcLabelPrefix = match[1];
      }
    }

    this.pendingSelection = null;

    this.toggle?.addEventListener('change', () => this.onToggle());

    this.querySelectorAll('input[name="gift_packaging_option"]').forEach((radio) => {
      radio.addEventListener('change', () => this.onOptionChange(radio));
    });

    this.querySelectorAll('input[name="gift_card_design"]').forEach((radio) => {
      radio.addEventListener('change', () => this.onCardDesignChange(radio));
    });

    // Capture phase so we can block an invalid gift selection before the
    // theme's own product-form submit handler runs.
    this.form?.addEventListener('submit', this.onFormSubmit.bind(this), true);

    // variant:add fires on the form itself right after the main product is
    // successfully added, and — unlike the generic cart:change event —
    // carries the actual line item that was just created, including its
    // unique `key`. We use that key to tag the gift item we add next, so
    // the cart drawer can later recognise this specific line item as
    // "already gift-wrapped" and hide its toggle accordingly.
    this.form?.addEventListener('variant:add', this.onVariantAdded.bind(this));

    this.updateCardSelectionClasses();
  }

  onToggle() {
    const isOn = this.toggle.checked;
    if (this.body) this.body.hidden = !isOn;
    if (!isOn) {
      this.reset();
      return;
    }
    const alreadySelected = this.querySelector('input[name="gift_packaging_option"]:checked');
    if (!alreadySelected) {
      this.modal?.show?.();
    }
  }

  onOptionChange(radio) {
    const requiresCard = radio.dataset.requiresCard === 'true';

    if (this.cardGrid) this.cardGrid.hidden = !requiresCard;
    if (this.messageField) this.messageField.hidden = !requiresCard;

    this.querySelectorAll('input[name="gift_card_design"]').forEach((input) => {
      input.required = requiresCard;
      if (!requiresCard) input.checked = false;
    });

    if (requiresCard && !this.querySelector('input[name="gift_card_design"]:checked')) {
      // Default to the last card design in the list — by convention the
      // store-branded card, regardless of whatever it's currently named.
      // Matching by exact text here would silently break every time the
      // variant gets renamed in Shopify admin.
      const allCards = [...this.querySelectorAll('.gift-packaging__card-thumb')];
      const defaultCard = allCards[allCards.length - 1];
      const defaultInput = defaultCard?.querySelector('input[name="gift_card_design"]');
      if (defaultInput) defaultInput.checked = true;
    }

    this.updateCardSelectionClasses();

    this.querySelectorAll('.gift-packaging__option').forEach((option) => {
      option.classList.toggle('is-selected', option.contains(radio));
    });

    const option = radio.closest('.gift-packaging__option');
    const title = option?.querySelector('.gift-packaging__option-title')?.textContent?.trim();
    const price = option?.querySelector('.gift-packaging__option-price')?.textContent?.trim();
    const thumbSrc = option?.querySelector('.gift-packaging__option-thumb img')?.getAttribute('src');

    if (this.summaryText && title) {
      this.summaryText.textContent = price ? `${title} — ${price}` : title;
    }
    if (this.summaryIcon && thumbSrc) {
      const img = this.summaryIcon.querySelector('img');
      if (img) img.src = thumbSrc;
    }

    this.clearError();
    this.modal?.hide?.();
    this.updateAtcButton(parseInt(radio.dataset.price, 10) || 0);
  }

  updateAtcButton(giftPriceCents) {
    // Never touch a disabled button (out of stock / unavailable). This
    // button is also managed by a third-party product-options app on this
    // store (visible via its "tpo_add_to_cart" class) — leaving disabled
    // buttons alone entirely avoids any chance of the two scripts
    // stepping on each other.
    if (!this.atcButton || this.atcButton.disabled || this.atcLabelPrefix === undefined) return;
    this.selectedGiftPrice = giftPriceCents || 0;
    const totalCents = this.basePrice + this.selectedGiftPrice;
    const formatted = (totalCents / 100).toFixed(2);
    this.atcButton.textContent = `${this.atcLabelPrefix}${formatted}`;
  }

  onCardDesignChange(selectedRadio) {
    this.updateCardSelectionClasses();
    this.clearError();
  }

  updateCardSelectionClasses() {
    this.querySelectorAll('.gift-packaging__card-thumb').forEach((thumb) => {
      const input = thumb.querySelector('input[name="gift_card_design"]');
      const isSelected = !!input?.checked;
      thumb.classList.toggle('is-selected', isSelected);

      const imgWrap = thumb.querySelector('.gift-packaging__card-thumb-img');
      const label = thumb.querySelector('.gift-packaging__card-thumb-label');

      if (imgWrap) {
        imgWrap.style.position = 'relative';
        imgWrap.style.border = isSelected ? '3px solid #000' : '2px solid #ddd';
        imgWrap.style.boxShadow = isSelected ? '0 0 0 2px #000' : 'none';
        imgWrap.style.transition = 'border-color .15s ease, box-shadow .15s ease';
      }

      if (label) {
        label.style.fontWeight = isSelected ? '700' : '400';
        label.style.color = isSelected ? '#000' : '#777';
      }
    });
  }

  getSelection() {
    if (!this.toggle?.checked) return null;

    const optionInput = this.querySelector('input[name="gift_packaging_option"]:checked');
    if (!optionInput) {
      return { invalid: true, reason: 'Please select a gifting option, or turn off "Is this a gift?".' };
    }

    const [type, variantId] = optionInput.value.split(':');

    if (type === 'gift_wrap') {
      const cardInput = this.querySelector('input[name="gift_card_design"]:checked');
      if (!cardInput) {
        return { invalid: true, reason: 'Please choose a card design.' };
      }
      const cardLabel = cardInput.closest('.gift-packaging__card-thumb')
        ?.querySelector('.gift-packaging__card-thumb-label')?.textContent?.trim();
      const message = this.messageField?.querySelector('textarea, input')?.value?.trim() || '';

      const properties = {
        'Gift for': this.dataset.productTitle || '',
        'Card design': cardLabel || '',
      };
      if (message) properties['Gift message'] = message;

      return { variantId: this.dataset.giftWrapVariant, properties };
    }

    if (type === 'gift_card') {
      return {
        variantId,
        properties: {
          'Gift for': this.dataset.productTitle || '',
        },
      };
    }

    return { invalid: true, reason: 'Please select a valid gifting option.' };
  }

  onFormSubmit(event) {
    const selection = this.getSelection();

    if (selection && selection.invalid) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showError(selection.reason);
      return;
    }

    this.clearError();
    this.pendingSelection = selection;
  }

  async onVariantAdded(event) {
    if (!this.pendingSelection) return;

    const selection = this.pendingSelection;
    this.pendingSelection = null;

    // The just-added main product's line item, straight from the /cart/add.js
    // response — this is where the real, stable `key` comes from.
    const mainLineItem = event.detail?.items?.[0];
    if (mainLineItem?.key) {
      selection.properties['_Wrapped item key'] = mainLineItem.key;
    }

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [
            {
              id: selection.variantId,
              quantity: 1,
              properties: selection.properties,
            },
          ],
        }),
      });

      if (!response.ok) throw new Error('Gift packaging could not be added');

      // Let the theme's own cart drawer/count components refresh themselves
      // the same safe way they already know how to (they fetch and render
      // their own correct markup internally) — we deliberately don't try to
      // hand-build the theme's internal cart:change event shape ourselves,
      // since guessing that shape wrong is what was crashing the drawer.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      this.showError(
        "Your item was added, but we couldn't add the gift packaging. Please add it from your cart."
      );
    }
  }

  reset() {
    this.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = false;
      input.required = false;
    });
    this.querySelectorAll('.gift-packaging__option, .gift-packaging__card-thumb').forEach((el) => {
      el.classList.remove('is-selected');
    });
    if (this.cardGrid) this.cardGrid.hidden = true;
    if (this.messageField) this.messageField.hidden = true;
    if (this.summaryText) this.summaryText.textContent = this.summaryText.dataset.defaultText || 'Select gifting preference';
    this.updateAtcButton(0);
    this.clearError();
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
  }
}

if (!window.customElements.get('gift-packaging')) {
  window.customElements.define('gift-packaging', GiftPackaging);
}